// Shared YouTube video list loader for the Videos page.
//
// Why: the Videos page used to start fetching only after the visitor opened
// it, showing "Loading videos…". Now the list is fetched once in the
// background (App.jsx), cached for the visit (sessionStorage), and the
// thumbnails are pre-downloaded — so the page opens with videos ready.

// AerialRoot Infotech YouTube channel
const CHANNEL_ID = 'UCJc9N7Yhet_L6x9bhL8uIjQ';
export const CHANNEL_URL = 'https://www.youtube.com/@aerialrootinfotech';
const FEED_URL = `https://www.youtube.com/feeds/videos.xml?channel_id=${CHANNEL_ID}`;

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
// Primary: our own backend fetches + caches the feed server-side (free, no API key, no shared quota).
const BACKEND_VIDEOS_URL = `${API_BASE}/api/videos`;
// Fallback 1: rss2json converts the channel RSS to JSON with CORS enabled (used only if backend is down).
const FALLBACK_API_URL = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(FEED_URL)}`;
// Fallback 2: generic CORS proxy + parse the raw Atom XML ourselves (used if rss2json also fails/rate-limited).
const FALLBACK2_URL = `https://api.allorigins.win/raw?url=${encodeURIComponent(FEED_URL)}`;

function parseAtomXml(xmlText) {
  const doc = new window.DOMParser().parseFromString(xmlText, 'text/xml');
  const entries = Array.from(doc.getElementsByTagNameNS('*', 'entry'));
  return entries
    .map(entry => {
      const id = entry.getElementsByTagNameNS('*', 'videoId')[0]?.textContent || null;
      const title = entry.getElementsByTagNameNS('*', 'title')[0]?.textContent || '';
      const published = entry.getElementsByTagNameNS('*', 'published')[0]?.textContent || '';
      return {
        id,
        title,
        date: formatDate(published),
        thumb: id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : '',
      };
    })
    .filter(v => v.id);
}

function videoIdFrom(item) {
  // guid looks like "yt:video:VIDEO_ID"; fall back to parsing the link
  if (item.guid && item.guid.includes('yt:video:')) return item.guid.split('yt:video:')[1];
  const m = (item.link || '').match(/[?&]v=([\w-]+)/);
  return m ? m[1] : null;
}

function formatDate(d) {
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
}


const SESSION_KEY = 'ar_videos_v1';
let cache = null;
let inflight = null;
let thumbsPreloaded = false;

try {
  const saved = sessionStorage.getItem(SESSION_KEY);
  if (saved) cache = JSON.parse(saved);
} catch (_) { /* storage unavailable — fine */ }

export function getCachedVideos() {
  return cache;
}

export function loadVideos() {
  if (inflight) return inflight;
  const tryBackend = () =>
    fetch(BACKEND_VIDEOS_URL)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`backend ${r.status}`))))
      .then(data => {
        const items = (Array.isArray(data) ? data : [])
          .map(it => ({
            id: it.id,
            title: it.title,
            date: formatDate(it.date),
            thumb: it.thumb || (it.id ? `https://i.ytimg.com/vi/${it.id}/hqdefault.jpg` : ''),
          }))
          .filter(v => v.id);
        if (items.length === 0) throw new Error('backend returned no videos');
        return items;
      });

  const tryRss2json = () =>
    fetch(FALLBACK_API_URL)
      .then(r => r.json())
      .then(data => {
        const items = (data.items || [])
          .map(it => ({
            id: videoIdFrom(it),
            title: it.title,
            date: formatDate(it.pubDate),
            thumb: it.thumbnail || (videoIdFrom(it) ? `https://i.ytimg.com/vi/${videoIdFrom(it)}/hqdefault.jpg` : ''),
          }))
          .filter(v => v.id);
        if (items.length === 0) throw new Error('rss2json returned no videos');
        return items;
      });

  const tryAllorigins = () =>
    fetch(FALLBACK2_URL)
      .then(r => r.text())
      .then(xmlText => {
        const items = parseAtomXml(xmlText);
        if (items.length === 0) throw new Error('allorigins returned no videos');
        return items;
      });

  inflight = tryBackend()
    .catch(err => {
      console.warn('[Videos] backend fetch failed, trying rss2json:', err.message);
      return tryRss2json();
    })
    .catch(err => {
      console.warn('[Videos] rss2json failed, trying allorigins+XML:', err.message);
      return tryAllorigins();
    })
    .then(items => {
      cache = items;
      try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(items)); } catch (_) {}
      return items;
    })
    .catch(err => {
      inflight = null; // allow retry later
      if (cache) return cache;
      throw err;
    });
  return inflight;
}

/** Download the first few thumbnails into the browser cache. */
export function preloadVideoThumbs(items, count = 9) {
  if (thumbsPreloaded || !items) return;
  thumbsPreloaded = true;
  items.slice(0, count).forEach(v => {
    const img = new Image();
    img.decoding = 'async';
    img.src = v.thumb;
  });
}

/** Open connections to YouTube early so the player starts faster on click. */
let warmed = false;
export function warmYouTube() {
  if (warmed) return;
  warmed = true;
  ['https://www.youtube.com', 'https://www.google.com', 'https://i.ytimg.com',
   'https://i9.ytimg.com', 'https://s.ytimg.com', 'https://yt3.ggpht.com']
    .forEach(href => {
      const l = document.createElement('link');
      l.rel = 'preconnect';
      l.href = href;
      l.crossOrigin = '';
      document.head.appendChild(l);
    });
}
